import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Alert, Col, Container, Dropdown, Row, Spinner, Stack } from "react-bootstrap";
import { ServiceManager } from "../../../../../services/ServiceManager";
import { APIStatusCode, MisType, StandardMappingTemplates } from "../../../../../app/enums";
import { SubmitHandler, useForm } from "react-hook-form";
import MISButton from "../../../../../components/button";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Layout from "../../../../layout";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import Skeleton from "react-loading-skeleton";
import IExtractClient from "../../../../../models/ExtractClient";
import IUserTypeMapping from "../../../../../models/misconfig/UsetTypeMapping";
import SimsUserTypeEdit from "../../../../../components/misconfig/sims/userTypes/sims-usertype-edit";
import ArborUserTypeEdit from "../../../../../components/misconfig/arbor/userTypes/arbor-usertype-edit";

export default function CreateUserTypeForm(props: any) {
    const { establishmentId, configurationId, userTypeId } = useParams();
    const queryClient = useQueryClient();
    const [selectedMappingTemplate, setSelectedMappingTemplate] = useState(StandardMappingTemplates.None);
    const [selectedUserType, setSelectedUserType] = useState(null);
    const [isApplicant, setIsAppliation] = useState(false);
    const [initialProviderSettings, setInitialProviderSettings] = useState<string>('');
    const lastProviderSettingsRef = useRef<string>('');

    let navigate = useNavigate();
    const providerConfigRef = useRef<any>(null);

    const { isLoading: isUserTypeMappingLoading, data: userTypeMapping } = useQuery({
        queryKey: [`mapping-user-types-${userTypeId}`],
        queryFn: () => ServiceManager.ConfigurationService.GetUserType(establishmentId, configurationId, userTypeId),
        enabled: !!(establishmentId && configurationId && userTypeId),
        refetchOnWindowFocus: false
    });

    const { isLoading: isUserTypesLoading, data: userTypes } = useQuery({
        queryKey: [`establishment-user-types-${establishmentId}`],
        queryFn: () => ServiceManager.UserTypeService.GetByEstablishmentId(establishmentId),
        enabled: !!establishmentId,
        refetchOnWindowFocus: false
    });   

    const { isLoading: isExtractClientLoading, data: extractClient } = useQuery<IExtractClient>({
        queryKey: [`extract-client-details`, establishmentId, configurationId],
        queryFn: () =>
            ServiceManager.ConfigurationService.GetConfigurationsById(establishmentId, configurationId),
        refetchOnWindowFocus: false,
    });    

    useEffect(() => {        
        if (userTypeMapping) {            
            handleUserTypeSelect(userTypeMapping.userTypeID);            
            handleMappingTemplateSelect(userTypeMapping.mappingTemplate);            
            handleIsApplicant(userTypeMapping.isApplicant);
            if(userTypeMapping.providerSettings) {
                lastProviderSettingsRef.current = userTypeMapping.providerSettings;
                setValue('providerSettings', userTypeMapping.providerSettings);
                setInitialProviderSettings(userTypeMapping.providerSettings);
            }
        } else {
            if(!userTypeId) {
                lastProviderSettingsRef.current = '';
                setValue('providerSettings', '');
                setInitialProviderSettings('');
            }
        }

        // eslint-disable-next-line
    }, [userTypeMapping, userTypeId]);   

     const mutation = useMutation({
        mutationFn: (model: IUserTypeMapping) => {
            return ServiceManager.ConfigurationService.CreateUserType(establishmentId, configurationId, model);
        },
        onError: (error, variables, context) => {

        },
        onSuccess: (data, variables, context) => {
            if (data === null) {
                ServiceManager.ToastService.showError("Unable to add user type.");
            }
            else {
                if (data.status === APIStatusCode.Created) {
                    ServiceManager.ToastService.showSuccess("User Type created successfully.");
                    queryClient.invalidateQueries({ queryKey: [`extract-client-user-types-${configurationId}`] });
                    if(data.response && data.response.data && data.response.data.userTypeID) {
                        queryClient.invalidateQueries({ queryKey: [`mapping-user-types-${data.response.data.userTypeID}`] });
                    }
                    navigate(-1);
                }
                else {
                    let errorMessage = (data.response && data.response.data) ? data.response.data : data.message;
                    ServiceManager.ToastService.showError(errorMessage.toString());
                }
            }
        }
    });

    const updatedMutation = useMutation({
        mutationFn: (model: IUserTypeMapping) => {
            return ServiceManager.ConfigurationService.UpdateUserType(establishmentId, configurationId, userTypeId, model);
        },
        onError: (error, variables, context) => {

        },
        onSuccess: (data, variables, context) => {
            if (data === null) {
                ServiceManager.ToastService.showError("Unable to update user type.");
            }
            else {
                if (data.status === APIStatusCode.Ok) {
                    ServiceManager.ToastService.showSuccess("User Type updated successfully.");
                    queryClient.invalidateQueries({ queryKey: [`extract-client-user-types-${configurationId}`] });
                    queryClient.invalidateQueries({ queryKey: [`mapping-user-types-${userTypeId}`] });
                    navigate(-1);
                }
                else {
                    let errorMessage = (data.response && data.response.data) ? data.response.data : data.message;
                    ServiceManager.ToastService.showError(errorMessage.toString());
                }
            }
        }
    });

    const { register, handleSubmit, clearErrors, setValue, formState: { errors } } = useForm<IUserTypeMapping>();
    const onSubmit: SubmitHandler<IUserTypeMapping> = (data) => {
        if(providerConfigRef.current) {
            const isValid = providerConfigRef.current.validate();
            if (!isValid) {
                return;
            }
        }
        if (userTypeId) {
            updatedMutation.mutate(data);
        }
        else {
            mutation.mutate(data);
        }
    };

    const firstCol = 2, secondCol = 4;

    const handleMappingTemplateSelect = (eventKey: number) => {
        setValue('mappingTemplate', eventKey);
        setSelectedMappingTemplate(eventKey);
        clearErrors('mappingTemplate');
    };

    const handleIsApplicant = (eventKey: boolean) => {
        setValue('isApplicant', eventKey);
        setIsAppliation(eventKey);
        clearErrors('isApplicant');
    };

    const handleUserTypeSelect = (eventKey: any) => {
        setValue('userTypeID', eventKey);
        setSelectedUserType(eventKey);
        clearErrors('userTypeID');
    }    

    const handleProviderSettingsChange = useCallback((value: string) => {
        if(lastProviderSettingsRef.current !== value) {
            lastProviderSettingsRef.current = value;
            setValue('providerSettings', value);
            clearErrors('providerSettings');
        }        
    }, [setValue, clearErrors]);

    return (
        <Layout title={userTypeId ? "Edit User Type For Extract " : "New User Type For Extract"}
            backArrow={<FontAwesomeIcon icon={faArrowLeft} onClick={() => navigate(-1)} className="me-4 fs-5 cursor-pointer" />}
        >
            <Stack>
                {!isUserTypesLoading && userTypes?.length === 0 &&
                    <Alert key={'danger'} variant={'danger'}>
                        No User Types set up for establishment
                    </Alert>
                }
                <form onSubmit={handleSubmit(onSubmit)} className="form">
                    {!isUserTypeMappingLoading &&
                        <Container className="mt-3">
                            <Row className="pb-3">
                                <Col md={firstCol}>User Type Name</Col>
                                <Col md={secondCol}>
                                    <Dropdown className="m-auto" onSelect={handleUserTypeSelect as any}>
                                        <Dropdown.Toggle className="btn btn-light border w-100 d-flex justify-content-between align-items-center"
                                            {...register('userTypeID', { required: 'User type is required' })}
                                            disabled={userTypeId ? true : false}>
                                            {selectedUserType && userTypes ? userTypes.find((x: any) => x.userTypeID === parseInt(selectedUserType)).userTypeName : 'Select'}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu className="timer-dropdown w-100">
                                            {!isUserTypesLoading && userTypes &&
                                                userTypes.map((userType: any) => (
                                                    <Dropdown.Item key={userType.userTypeID} eventKey={userType.userTypeID}>
                                                        {userType.userTypeName}
                                                    </Dropdown.Item>
                                                ))
                                            }
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    {errors.userTypeID && <div className="invalid-feedback" style={{ display: 'block', color: '#DC3245' }}>User Type is required</div>}
                                </Col>
                            </Row>
                            <Row className="pb-3">
                                <Col md={firstCol}>Mapping Template</Col>
                                <Col md={secondCol}>
                                    <Dropdown className="m-auto" onSelect={handleMappingTemplateSelect as any}>
                                        <Dropdown.Toggle className="btn btn-light border w-100 d-flex justify-content-between align-items-center"
                                            {...register('mappingTemplate', { required: 'Mapping template is required' })}>
                                            {selectedMappingTemplate ? StandardMappingTemplates[selectedMappingTemplate] : 'Select'}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu className="timer-dropdown w-100">
                                            <Dropdown.Item eventKey={StandardMappingTemplates.None}>None</Dropdown.Item>
                                            <Dropdown.Item eventKey={StandardMappingTemplates.Student}>Student</Dropdown.Item>
                                            <Dropdown.Item eventKey={StandardMappingTemplates.Staff}>Staff</Dropdown.Item>
                                            <Dropdown.Item eventKey={StandardMappingTemplates.Applicants}>Applicants</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    {errors.mappingTemplate && <div className="invalid-feedback" style={{ display: 'block', color: '#DC3245' }}>Mapping Template is required</div>}
                                </Col>
                            </Row>
                            <Row className="pb-3">
                                <Col md={firstCol}>Is Applicant</Col>
                                <Col md={secondCol}>
                                    <input type="checkbox" {...register("isApplicant")} defaultChecked={isApplicant} className={`mt-1 ms-0 ${(errors.isApplicant) ? 'is-invalid' : ''}`} />
                                </Col>
                            </Row>
                            {!isExtractClientLoading && extractClient &&                                
                                <div>                                    
                                    <input type="hidden" { ...register('providerSettings') } />
                                    {extractClient.misType === MisType.Sims ?
                                        <SimsUserTypeEdit
                                            key={`sims-${userTypeId || 'new'}`}
                                            ref={providerConfigRef}
                                            configJson={initialProviderSettings}
                                            setJsonString={handleProviderSettingsChange}
                                            firstCol={firstCol}
                                            secondCol={secondCol} />
                                    : extractClient.misType === MisType.Arbor ?
                                        <ArborUserTypeEdit
                                            key={`arbor-${userTypeId || 'new'}`}
                                            ref={providerConfigRef}
                                            configJson={initialProviderSettings}
                                            selectedUserType={ selectedMappingTemplate }
                                            isNew={ !userTypeId }
                                            setJsonString={handleProviderSettingsChange}
                                            firstCol={firstCol}
                                            secondCol={secondCol} />
                                    : null
                                    }                                    
                                </div>
                            }
                            {
                                !(mutation.isPending || updatedMutation.isPending)
                                    ? (
                                        <Row className="mt-5">
                                            <Col md={firstCol}></Col>
                                            <Col md={secondCol}>
                                                <MISButton text="Cancel" className="btn popup-btn right-margin10 btn-outline-secondary" type="button" FnOnClick={() => navigate(-1)} />
                                                <MISButton text={userTypeId ? "Update" : "Create"} disabled={(!isUserTypesLoading && userTypes.length === 0) ? true : false} className="popup-btn btn btn-primary crbc-bg-color" type="submit" FnOnClick={handleSubmit(onSubmit)} />
                                            </Col>
                                        </Row>
                                    )
                                    : (
                                        <Row>
                                            <Col md={firstCol}></Col>
                                            <Col md={secondCol}>
                                                <div className="d-flex justify-content-center mt-3">
                                                    <Spinner animation="border" variant="info" role="status" > </Spinner>
                                                </div>
                                            </Col>
                                        </Row>
                                    )
                            }
                        </Container>
                    }

                    {isUserTypeMappingLoading &&
                        <React.Fragment>
                            <div className="w-25 mis-loader">
                                <Skeleton height={20} count={5} />
                            </div>
                        </React.Fragment>
                    }
                </form>
            </Stack>
        </Layout>
    );
}